import 'prismjs'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/themes/prism-tomorrow.css'
import 'src/styles/globals.css'
import '@buyscout/components/theme.css'
import '@buyscout/components/index.css'
import '@buyscout/components/editor.css'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { loadComponentsLocale } from '@buyscout/components'
import { dualTokenStorage, installChunkReloadHandler } from '@buyscout/shared'
import * as Sentry from '@sentry/react'
import { AppErrorBoundary } from 'src/components/AppErrorBoundary'
import { detectInitialLocale } from 'src/i18n/instance'

// Imports run in order, so the ticket leaves the address bar before Sentry can
// record it.
/* eslint-disable perfectionist/sort-imports */
import './utils/connect-ticket-bootstrap'
import './utils/sentry.ts'
/* eslint-enable perfectionist/sort-imports */

installChunkReloadHandler(reason => {
  Sentry.captureMessage('Chunk preload failed; reloading', {
    level: 'warning',
    extra: { reason: reason instanceof Error ? reason.message : String(reason) },
  })
})

function clearStaleMockAuth(): void {
  if (dualTokenStorage.get()?.accessToken?.endsWith('.mock-signature')) {
    dualTokenStorage.clear()
  }
}

async function bootstrap() {
  if (import.meta.env.DEV) {
    if (import.meta.env.VITE_USE_MOCKS === 'true' || import.meta.env.VITE_USE_MOCKS === '1') {
      try {
        const { enableMocks } = await import('./mocks/start')
        await enableMocks()
      } catch (error) {
        const alert = document.createElement('p')
        alert.setAttribute('role', 'alert')
        alert.textContent = `Mock preview could not start: ${error instanceof Error ? error.message : String(error)}`
        document.getElementById('root')!.replaceChildren(alert)
        return
      }
    } else {
      clearStaleMockAuth()
    }
  }

  // The boot view's line is already localized in index.html; React's copy of it must not
  // render English while the catalog is still loading.
  const [{ default: App }] = await Promise.all([import('./App'), loadComponentsLocale(detectInitialLocale())])
  const router = createBrowserRouter([{ path: '*', element: <AppErrorBoundary><App /></AppErrorBoundary> }])
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>,
  )
}

bootstrap().catch(error => {
  console.error('Failed to bootstrap the application', error)
})
